import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import CanonicalTag from "@/components/CanonicalTag";

const CaliforniaADU = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "name": "UDA ADU",
    "url": "https://www.udaadu.com/california-adu",
    "logo": "https://www.udaadu.com/logo.png",
    "image": "https://www.udaadu.com/logo.png",
    "description": "UDA ADU specializes in high-quality, turnkey Accessory Dwelling Units (ADUs) across California. From permitting to move-in, we handle everything with expert craftsmanship and modern technology.",
    "email": "info@udaadu.com",
    "telephone": "+1-408-912-8989",
    "areaServed": {
      "@type": "Place",
      "name": "California"
    },
    "founder": {
      "@type": "Person",
      "name": "Mona Poudat"
    },
    "foundingDate": "2014-01-28",
    "legalName": "UDA ADU",
    "sameAs": [
      "https://www.instagram.com/uda__adu/"
    ]
  };
  
  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/california-adu" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>}

      <main className="max-w-4xl mx-auto px-4 py-8">
        <section className="space-y-8">
          {/* Hero Section */}
          <div className="text-center space-y-4 my-[29px] mt-8 md:mt-[39px] mt-10 md:mt-[39px]">
            <h1 className="text-4xl md:text-4xl lg:text-5xl font-light mb-6 text-[#e4683b] mt-2 md:mt-0 my-0 py-0">California Trusted ADU Builder</h1>
            <p className="text-lg md:text-lg leading-relaxed max-w-3xl mx-auto text-white mt-2 md:mt-0 text-base my-0 py-0">
              Looking to build an ADU in California? <strong>UDA ADU</strong> specializes in high-quality, turnkey Accessory Dwelling Units designed for local zoning, budget, and lifestyle needs. From permit to move-in, we handle it all — fast, clean, and stress-free.
            </p>
          </div>

          {/* Updated Video Section */}
          <section style={{
          padding: '60px 0'
        }} className="my-[100px]">
            <div className="text-center my-0 py-[14px] text-[#e4683b] text-4xl">
              <h2 style={{
              color: 'white',
              fontSize: '2rem',
              marginBottom: '20px'
            }} className="text-base md:text-4xl text-[#e4683b] text-lg my-[6px]">
                See One of Our Most Popular ADU Models
              </h2>

              <p style={{
              color: 'white',
              fontSize: '1.1rem',
              maxWidth: '800px',
              marginBottom: '40px',
              margin: '0 auto 40px auto'
            }} className="md:text-lg text-white text-lg my-[32px]">
                This 3D walkthrough shows how our California ADU model can adapt to a variety of exterior finishes — modern, timeless, and tailored to your lot.
              </p>

              <video autoPlay muted loop playsInline disablePictureInPicture onContextMenu={() => false} controlsList="nodownload nofullscreen noremoteplay" style={{
              width: '100%',
              maxWidth: '960px',
              borderRadius: '20px',
              boxShadow: '0 0 24px rgba(0,0,0,0.2)',
              marginBottom: '60px',
              pointerEvents: 'none'
            }}>
                <source src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/uda-santa-clara-model-adu-video.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJWSURFT1MvdWRhLXNhbnRhLWNsYXJhLW1vZGVsLWFkdS12aWRlby5tcDQiLCJpYXQiOjE3NDk4MjM0NjYsImV4cCI6MjA2NTE4MzQ2Nn0.6j9a3WgKBNaWvqzI8BYfSDDzkmYFno9qllvOCEmBKWc" type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              <div style={{
              marginTop: '40px'
            }}>
                <Button onClick={openCalendlyModal} className="bg-[#e4683b] hover:bg-[#d55a32] text-white text-sm md:text-2xl py-3 px-6 md:py-4 md:px-8 rounded-lg transition-colors font-normal">
                  Book Your Complimentary ADU Consultation
                </Button>
              </div>
            </div>
          </section>

          {/* Why Choose UDA ADU */}
          <div className="bg-white rounded-lg p-8 shadow-sm border">
            <h2 className="text-lg md:text-2xl font-light text-[#e4683b] mb-6">Why Choose UDA ADU?</h2>
            <ul className="space-y-3 text-xs md:text-base text-black">
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-semibold">✅</span>
                <span>100% Focused on ADUs (No general contracting distractions)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-semibold">✅</span>
                <span>Local experts in California zoning and planning</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-semibold">✅</span>
                <span>
                  Transparent pricing with our{" "}
                  <Link to="/adu-calculator" className="text-[#e4683b] hover:underline">
                    ADU Cost Calculator
                  </Link>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-semibold">✅</span>
                <span>Fully licensed, bonded, and insured (CSLB #1146840)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-semibold">✅</span>
                <span>Modern, pre-permitted floorplans — ready to build</span>
              </li>
            </ul>
          </div>

          {/* Image Gallery Section */}
          <div className="flex flex-col md:flex-row gap-4 my-[52px]">
            {/* Left side - Vertical image */}
            <div className="w-full md:w-1/2">
              <AspectRatio ratio={3 / 4} className="bg-muted rounded-lg overflow-hidden">
                <img src="/lovable-uploads/99cde6f3-76e0-48da-8ece-20b8837d348b.png" alt="Contemporary ADU with white box architecture, full-height sliding glass doors, and open-concept indoor-outdoor living space in California." className="object-cover w-full h-full" />
              </AspectRatio>
            </div>
            
            {/* Right side - Two horizontal images */}
            <div className="w-full md:w-1/2 flex flex-col gap-4">
              <AspectRatio ratio={3 / 2} className="bg-muted rounded-lg overflow-hidden">
                <img src="/lovable-uploads/7d021e95-bfde-4023-8266-6c8479134814.png" alt="Modern backyard ADU in California featuring black metal siding, large sliding glass doors, and a landscaped patio with outdoor dining and succulents." className="object-cover w-full h-full" />
              </AspectRatio>
              
              <AspectRatio ratio={3 / 2} className="bg-muted rounded-lg overflow-hidden">
                <img src="/lovable-uploads/af091130-a7f0-4012-ae37-c1a6eb99917b.png" alt="Modern craftsman-style ADU with white stucco exterior, black window framing, and a warm natural wood front door, framed by low-maintenance native landscaping" className="object-cover w-full h-full" />
              </AspectRatio>
            </div>
          </div>

          {/* Areas We Serve */}
          <div className="bg-gray-50 rounded-lg p-8 my-[52px]">
            <h2 className="text-lg md:text-2xl font-light text-[#e4683b] mb-4">Areas We Serve in California</h2>
            <p className="text-xs md:text-base text-black mb-2">
              Bay Area: San Jose, Santa Clara, Sunnyvale, Cupertino, Los Gatos, Campbell, Palo Alto, Mountain View, Los Altos, Saratoga, Menlo Park, Atherton, Redwood City, San Mateo, Belmont, San Carlos, Burlingame
            </p>
            <p className="text-xs md:text-base text-black">
              <em>Need a specific city?</em> We also serve many other California cities —{" "}
              <Link to="/contact-us" className="text-[#e4683b] hover:underline">
                contact us to learn more →
              </Link>
            </p>
          </div>

          {/* Popular Floorplans */}
          <div className="space-y-4 my-[60px]">
            <h2 className="text-lg md:text-2xl font-light text-[#e4683b]">Popular Floorplans We Build</h2>
            <ul className="space-y-2 text-xs md:text-base text-white ml-4">
              <li>• Studio ADUs (250–450 sq ft)</li>
              <li>• 1-Bedroom ADUs (450–650 sq ft)</li>
              <li>• 2-Bedroom ADUs (700–1,000+ sq ft)</li>
            </ul>
            <p className="text-center text-lg my-[49px] text-[#e4683b] md:text-3xl">
              👉{" "}
              <Link to="/our-work" className="text-[#e4683b] hover:underline py-0 my-0">
                View ADU Designs and Gallery
              </Link>
            </p>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-lg p-8 shadow-sm border space-y-6 my-[48px]">
            <h2 className="text-lg md:text-2xl font-light text-[#e4683b]">Frequently Asked Questions</h2>
            
            <div>
              <p className="text-xs md:text-base text-black">
                <strong>Q:</strong> How long does it take to build an ADU in California?
              </p>
              <p className="text-xs md:text-base text-black mt-1">
                <strong>A:</strong> From design to final inspection, most UDA projects take 4–6 months total.
              </p>
            </div>

            <div>
              <p className="text-xs md:text-base text-black">
                <strong>Q:</strong> Do I need to talk to the city?
              </p>
              <p className="text-xs md:text-base text-black mt-1">
                <strong>A:</strong> We handle all communication with the city, including permits and inspections.
              </p>
            </div>

            <div>
              <p className="text-xs md:text-base text-black">
                <strong>Q:</strong> Can I use my ADU for rental income?
              </p>
              <p className="text-xs md:text-base text-black mt-1">
                <strong>A:</strong> Yes! Many California homeowners earn $2,000–$4,500/month with our ADUs.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-[#e4683b]/5 rounded-lg p-8 space-y-4 my-[52px]">
            
            <p className="mb-6 md:text-lg text-white text-2xl">
              Ready to build your ADU in California?
            </p>
            <Button onClick={openCalendlyModal} className="bg-[#e4683b] hover:bg-[#d55a32] text-white text-sm md:text-2xl py-3 px-6 md:py-4 md:px-8 rounded-lg transition-colors font-normal my-[23px]">
              Book Your Complimentary Meeting
            </Button>
            <p className="mt-4 md:text-lg text-white text-sm my-[29px]">
              Or use our{" "}
              <Link to="/adu-calculator" className="text-[#e4683b] hover:underline">
                ADU Cost Calculator
              </Link>
              {" "}to get started in 60 seconds.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CaliforniaADU;
